import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lideranca } from '@eleg360/types';
import { liderancasApi } from '@/lib/api/liderancas';
import {
  compromissosVotosApi,
  type CompromissoBairroApi,
} from '@/lib/api/compromissos-votos';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { exportarCsv } from '@/lib/csv';
import { Info } from '@/design-system/Info';

/**
 * Controle de votação (proto:1707-1716 — RENDER.controle é redefinida duas
 * vezes no protótipo, em 959 e 1707; a de 1707 vence e é a que esta tela
 * segue).
 *
 * HONESTIDADE DE DADO — o que este componente NÃO faz, de propósito:
 * o protótipo compara o prometido com `cvHist(bairro, ano)`, uma série
 * histórica de votos por bairro e por ano. Essa série não existe nos dados
 * reais: a apuração do TSE (`eleg.detalhe_zona`/`resultado_zona`) é por ZONA
 * ELEITORAL, não por bairro, e não há tabela de mapeamento zona↔bairro em
 * nenhum lugar do schema — mesma lacuna já declarada em
 * `MatrizForcaMomento.tsx` (task 4.2). Por isso não há coluna de "votos
 * históricos", "diferença calculada" nem seletor de ano ligado ao TSE: a
 * lacuna é declarada explicitamente na seção "Comparação com o TSE" abaixo,
 * em vez de mostrar um número fabricado.
 *
 * O que É real: o compromisso de votos que cada liderança assume por
 * território e ciclo (`eleg.compromisso_lideranca`, cadastro do próprio
 * gabinete). O seletor "Eleição base" escolhe o CICLO desse compromisso —
 * não um ano de histórico do TSE, que não temos por localidade.
 */

const anoAtual = new Date().getFullYear();
// Eleições no Brasil caem em anos pares (municipal/geral). Não é dado do TSE:
// é só o valor inicial do formulário, que o gabinete pode trocar livremente.
const CICLO_PADRAO = anoAtual % 2 === 0 ? anoAtual : anoAtual + 1;

export function ControleVotacaoPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [ciclo, setCiclo] = useState(CICLO_PADRAO);
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [buscaVinculo, setBuscaVinculo] = useState<Record<string, string>>({});
  const [rascunho, setRascunho] = useState<Record<string, string>>({});

  const { data: resumo, isLoading } = useQuery({
    queryKey: ['compromissos-votos', 'resumo', ciclo],
    queryFn: () => compromissosVotosApi.resumo(token, ciclo),
    enabled: !!token,
  });

  const { data: liderancas } = useQuery({
    queryKey: ['liderancas', 'todas'],
    queryFn: () => liderancasApi.list({}, token),
    enabled: !!token,
  });

  const { data: todosCompromissos } = useQuery({
    queryKey: ['compromissos-votos', 'todos'],
    queryFn: () => compromissosVotosApi.list(token),
    enabled: !!token,
  });

  const ciclosConhecidos = useMemo(() => {
    const s = new Set<number>([CICLO_PADRAO, ciclo]);
    (todosCompromissos ?? []).forEach((c) => s.add(c.ciclo));
    return [...s].sort((a, b) => a - b);
  }, [todosCompromissos, ciclo]);

  const vincular = useMutation({
    mutationFn: (vars: { lideranca_id: string; bairro: string }) =>
      compromissosVotosApi.create(
        { lideranca_id: vars.lideranca_id, bairro: vars.bairro, ciclo, votos_prometidos: 0 },
        token,
      ),
    onSuccess: (_dado, vars) => {
      queryClient.invalidateQueries({ queryKey: ['compromissos-votos'] });
      const l = liderancas?.find((x) => x.id === vars.lideranca_id);
      toast(`${l?.nome ?? 'Liderança'} vinculada a ${vars.bairro}.`);
    },
    onError: (err: any) => {
      const m = err?.response?.data?.message;
      toast(Array.isArray(m) ? m.join(' · ') : m || 'Erro ao vincular liderança.');
    },
  });

  const atualizarVotos = useMutation({
    mutationFn: (vars: { id: string; votos_prometidos: number }) =>
      compromissosVotosApi.update(vars.id, { votos_prometidos: vars.votos_prometidos }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compromissos-votos'] });
    },
    onError: (err: any) => {
      const m = err?.response?.data?.message;
      toast(Array.isArray(m) ? m.join(' · ') : m || 'Erro ao salvar os votos prometidos.');
    },
    // Sucesso ou erro, o rascunho local não deve sobreviver à mutation: se
    // deu certo, o valor já veio do servidor via invalidateQueries; se deu
    // erro, o campo precisa voltar a mostrar o valor real (do servidor), não
    // o valor digitado que nunca foi salvo.
    onSettled: (_dado, _erro, vars) => {
      setRascunho((prev) => {
        const proximo = { ...prev };
        delete proximo[vars.id];
        return proximo;
      });
    },
  });

  const remover = useMutation({
    mutationFn: (id: string) => compromissosVotosApi.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compromissos-votos'] });
      toast('Vínculo removido.');
    },
  });

  const toggle = (bairroId: string) => {
    setAbertos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(bairroId)) proximo.delete(bairroId);
      else proximo.add(bairroId);
      return proximo;
    });
  };

  const linhas = resumo ?? [];
  const buscaLower = busca.trim().toLowerCase();
  const linhasFiltradas = buscaLower
    ? linhas.filter((r) =>
        r.compromissos.some((c) => c.lideranca_nome.toLowerCase().includes(buscaLower)) ||
        r.bairro.toLowerCase().includes(buscaLower),
      )
    : linhas;

  const totalPrometido = linhas.reduce((acc, r) => acc + r.total_prometido, 0);
  const totalLiderancas = linhas.reduce((acc, r) => acc + r.compromissos.length, 0);
  const semLideranca = linhas.filter((r) => r.compromissos.length === 0);

  const candidatasParaVinculo = (bairro: CompromissoBairroApi, termo: string): Lideranca[] => {
    const q = termo.trim().toLowerCase();
    if (!q) return [];
    const jaVinculadas = new Set(bairro.compromissos.map((c) => c.lideranca_id));
    return (liderancas ?? [])
      .filter((l) => !jaVinculadas.has(l.id))
      .filter(
        (l) =>
          l.nome.toLowerCase().includes(q) ||
          (l.tipo ?? '').toLowerCase().includes(q) ||
          (l.bairro ?? '').toLowerCase().includes(q),
      )
      .slice(0, 6);
  };

  return (
    <div className="page" id="p-controle">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Controle de votação</h1>
          <p>
            Compromisso de votos que cada liderança assume por território, para o ciclo eleitoral
            selecionado. Vincule lideranças a cada localidade, mesmo que não morem nela, e
            registre quantos votos cada uma promete entregar.
          </p>
        </div>
        <div className="act">
          <label htmlFor="cv-ciclo" style={{ fontSize: 12, color: 'var(--ink3)' }}>
            Eleição base
          </label>
          <select
            id="cv-ciclo"
            className="sel"
            aria-label="Eleição base"
            value={ciclo}
            onChange={(e) => setCiclo(Number(e.target.value))}
          >
            {ciclosConhecidos.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <input
            type="search"
            role="searchbox"
            aria-label="Buscar liderança"
            placeholder="Filtrar por liderança ou território…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <button
            className="btn"
            disabled={linhas.length === 0}
            onClick={() =>
              exportarCsv(
                'controle-votacao',
                [
                  { cabecalho: 'Território', valor: (r: CompromissoBairroApi) => r.bairro },
                  {
                    cabecalho: 'Lideranças vinculadas',
                    valor: (r: CompromissoBairroApi) => r.compromissos.length,
                  },
                  {
                    cabecalho: 'Votos prometidos',
                    valor: (r: CompromissoBairroApi) => r.total_prometido,
                  },
                ],
                linhas,
              )
            }
          >
            Exportar
          </button>
        </div>
      </div>

      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Compromisso das lideranças</div>
          <div className="v">{totalPrometido.toLocaleString('pt-BR')}</div>
          <div className="d">{totalLiderancas} lideranças vinculadas</div>
        </div>
        <div className="card kpi">
          <div className="l">Localidades sem liderança</div>
          <div className="v" style={{ color: semLideranca.length ? 'var(--crit)' : 'var(--good)' }}>
            {semLideranca.length}
          </div>
          <div className="d">{semLideranca.map((r) => r.bairro).join(', ') || '—'}</div>
        </div>
        <div className="card kpi">
          <div className="l">Ciclo em edição</div>
          <div className="v">{ciclo}</div>
          <div className="d">compromissos deste ciclo</div>
        </div>
        <div className="card kpi">
          <div className="l">Territórios acompanhados</div>
          <div className="v">{linhas.length}</div>
          <div className="d">territórios cadastrados no tenant</div>
        </div>
      </div>

      <div className="card flat tw" style={{ marginTop: 12 }}>
        {isLoading ? (
          <div className="empty">Carregando compromissos de votos…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Território</th>
                <th className="num">Lideranças</th>
                <th className="num">
                  Votos prometidos{' '}
                  <Info titulo="De onde vem o número de votos prometidos">
                    Cada liderança tem os votos prometidos ajustados diretamente pelo gabinete. Não
                    há estimativa automática, porque não existe base histórica por localidade para
                    derivar uma.
                  </Info>
                </th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasFiltradas.map((r) => {
                const aberto = abertos.has(r.bairro_id || r.bairro);
                const chaveBairro = r.bairro_id || r.bairro;
                const termoVinculo = buscaVinculo[chaveBairro] || '';
                const candidatas = candidatasParaVinculo(r, termoVinculo);
                return (
                  <Fragment key={chaveBairro}>
                    <tr
                      className="row"
                      onClick={() => toggle(chaveBairro)}
                    >
                      <td style={{ color: 'var(--ink3)' }}>{aberto ? '▾' : '▸'}</td>
                      <td>
                        <b>{r.bairro}</b>
                      </td>
                      <td className="num">{r.compromissos.length}</td>
                      <td className="num">
                        <b>{r.total_prometido.toLocaleString('pt-BR')}</b>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn sm"
                          onClick={() => setAbertos((prev) => new Set(prev).add(chaveBairro))}
                        >
                          + Liderança
                        </button>
                      </td>
                    </tr>
                    {aberto && (
                      <tr>
                        <td></td>
                        <td colSpan={4} style={{ background: 'var(--surface2)', padding: '8px 12px' }}>
                          <table style={{ width: '100%' }}>
                            <thead>
                              <tr>
                                <th>Liderança</th>
                                <th>Tipo</th>
                                <th>Votos prometidos</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.compromissos.length === 0 && (
                                <tr>
                                  <td colSpan={4} style={{ color: 'var(--ink3)' }}>
                                    Nenhuma liderança vinculada
                                  </td>
                                </tr>
                              )}
                              {r.compromissos.map((c) => {
                                const draft = rascunho[c.id] ?? String(c.votos_prometidos);
                                return (
                                  <tr key={c.id}>
                                    <td>
                                      <b>{c.lideranca_nome}</b>
                                    </td>
                                    <td>{c.lideranca_tipo}</td>
                                    <td>
                                      <input
                                        id={`cv-votos-${c.id}`}
                                        type="number"
                                        role="spinbutton"
                                        aria-label={`Votos prometidos de ${c.lideranca_nome}`}
                                        className="inline-edit num"
                                        min={0}
                                        value={draft}
                                        onChange={(e) =>
                                          setRascunho((prev) => ({ ...prev, [c.id]: e.target.value }))
                                        }
                                        onBlur={() => {
                                          const v = parseInt(draft.replace(/\D/g, ''), 10) || 0;
                                          if (v !== c.votos_prometidos) {
                                            atualizarVotos.mutate({ id: c.id, votos_prometidos: v });
                                          }
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <button className="btn sm" onClick={() => remover.mutate(c.id)}>
                                        ✕
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr>
                                <td colSpan={4} style={{ paddingTop: 8 }}>
                                  <input
                                    placeholder="Buscar liderança por nome, tipo ou território para vincular…"
                                    value={termoVinculo}
                                    onChange={(e) =>
                                      setBuscaVinculo((prev) => ({
                                        ...prev,
                                        [chaveBairro]: e.target.value,
                                      }))
                                    }
                                    style={{ width: '100%' }}
                                  />
                                  {candidatas.length > 0 && (
                                    <div style={{ marginTop: 4 }}>
                                      {candidatas.map((l) => (
                                        <div
                                          key={l.id}
                                          className="row"
                                          style={{
                                            padding: '6px 8px',
                                            display: 'flex',
                                            gap: 8,
                                            alignItems: 'center',
                                            borderBottom: '1px solid var(--line)',
                                            cursor: 'pointer',
                                          }}
                                          onClick={() => {
                                            vincular.mutate({ lideranca_id: l.id, bairro: r.bairro });
                                            setBuscaVinculo((prev) => ({ ...prev, [chaveBairro]: '' }));
                                          }}
                                        >
                                          <b style={{ flex: 1 }}>{l.nome}</b>
                                          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
                                            {l.tipo}
                                            {l.bairro && l.bairro !== r.bairro
                                              ? ` · mora em ${l.bairro}`
                                              : ''}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {linhasFiltradas.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">
                    Nenhum território encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h2>Comparação com o histórico do TSE</h2>
        <div style={{ padding: '12px 8px', color: 'var(--ink3)', fontSize: 13, lineHeight: 1.5 }}>
          A diferença entre o prometido e o histórico do TSE não está disponível: a apuração
          oficial é registrada por zona eleitoral, e não há mapeamento entre zona e território.{' '}
          <Info titulo="O que falta para esta comparação existir">
            O histórico mora em <code>eleg.detalhe_zona</code> e{' '}
            <code>eleg.resultado_zona</code>, sempre por zona. Assim que a correspondência entre
            zona e território existir, esta seção passa a mostrar o histórico e a diferença lado a
            lado com o compromisso das lideranças.
          </Info>
        </div>
      </div>

    </div>
  );
}
